import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Container } from '../ui';

const FooterWrapper = styled.footer`
  background: #0f0f0f; color: white; padding: 4rem 0 2rem;
`;

const FooterGrid = styled.div`
  display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 3rem;
  @media (max-width: 768px) { grid-template-columns: 1fr 1fr; gap: 2rem; }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const Brand = styled.div`
  @media (max-width: 768px) { grid-column: 1 / -1; }
`;

const BrandName = styled.div`
  font-size: 1.25rem; font-weight: 700; margin-bottom: 1rem;
  display: flex; align-items: center; gap: 0.5rem;
`;

const LogoMark = styled.span`
  width: 28px; height: 28px; background: #eab308; border-radius: 0.25rem;
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 0.875rem; color: #0f0f0f;
`;

const Tagline = styled.p`
  color: #6b7280; font-size: 0.875rem; line-height: 1.625; max-width: 280px;
`;

const Heading = styled.h4`
  font-size: 0.875rem; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.05em; color: #6b7280; margin-bottom: 1rem;
`;

const Links = styled.ul`
  list-style: none; display: flex; flex-direction: column; gap: 0.75rem;
`;

const FLink = styled(Link)`
  color: #e5e7eb; font-size: 0.875rem;
  &:hover { color: white; }
`;

const ExtLink = styled.a`
  color: #e5e7eb; font-size: 0.875rem;
  &:hover { color: white; }
`;

const Divider = styled.hr`
  border: none; border-top: 1px solid #374151; margin: 3rem 0 1.5rem;
`;

const Bottom = styled.div`
  display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 1rem;
`;

const Copy = styled.p`
  color: #6b7280; font-size: 0.875rem; margin: 0;
`;

const Socials = styled.div`
  display: flex; gap: 1rem;
`;

const Social = styled.a`
  color: #6b7280; &:hover { color: white; }
  svg { width: 20px; height: 20px; }
`;

const Footer = () => (
  <FooterWrapper>
    <Container>
      <FooterGrid>
        <Brand>
          <BrandName><LogoMark>5</LogoMark>The New Calendar</BrandName>
          <Tagline>The first calendar in history built with math, science, and logic.</Tagline>
        </Brand>
        <div>
          <Heading>Shop</Heading>
          <Links>
            <li><FLink to="/shop">All Products</FLink></li>
            <li><FLink to="/newcalendar_classic">Classic Calendar</FLink></li>
            <li><FLink to="/newcalendar_wallmap">Wall Map</FLink></li>
          </Links>
        </div>
        <div>
          <Heading>Learn</Heading>
          <Links>
            <li><FLink to="/about">About</FLink></li>
            <li><FLink to="/media">Media</FLink></li>
            <li><FLink to="/time">Time Converter</FLink></li>
          </Links>
        </div>
        <div>
          <Heading>Connect</Heading>
          <Links>
            <li><ExtLink href="https://tiktok.com/@thenewcalendar" target="_blank" rel="noopener">TikTok</ExtLink></li>
            <li><ExtLink href="https://instagram.com/thenewcalendar" target="_blank" rel="noopener">Instagram</ExtLink></li>
            <li><ExtLink href="mailto:tom@thenewcalendar.com">Email Us</ExtLink></li>
          </Links>
        </div>
      </FooterGrid>
      <Divider />
      <Bottom>
        <Copy>© {new Date().getFullYear()} The New Calendar. All rights reserved.</Copy>
        <Socials>
          <Social href="https://tiktok.com/@thenewcalendar" target="_blank" rel="noopener" aria-label="TikTok">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/></svg>
          </Social>
          <Social href="https://instagram.com/thenewcalendar" target="_blank" rel="noopener" aria-label="Instagram">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
          </Social>
        </Socials>
      </Bottom>
    </Container>
  </FooterWrapper>
);

export default Footer;
